import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bars3Icon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import { isValidUrl } from "../utils/utilMethods";
import personIcon from "../assets/icons/personIcon.svg"; 

interface HeaderProps {
  onOpenMobileMenu: () => void;
}

const PAGE_TITLES: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/profile-list": "Profile list",
  "/marketPlaceCards": "Marketplace Cards",
  "/events": "Events",
  "/chat": "Chat",
  "/history": "History",
  "/mydeals": "My Deals",
  "/feeds": "Feeds",
  "/settings": "Account settings",
  "/profile": "Profile",
  "/opportunities": "Opportunities",
  "/notifications": "Notifications",
  "/knowledge-hub": "Knowledge hub",
};

const SETTINGS_TAB_TITLES: Record<string, string> = {
  profile: "Profile",
  language: "Language preference",
  notifications: "Notification preference",
  privacy: "Privacy settings",
  linkAccount: "Link account",
  about: "About",
  terms: "Terms & conditions",
};

const getSavedUser = () => {
  try {
    const userJson = localStorage.getItem("loginResponse");
    return userJson ? JSON.parse(userJson) : null;
  } catch {
    return null;
  }
};

export const Header: React.FC<HeaderProps> = ({ onOpenMobileMenu }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);

  const userProfile = getSavedUser();
  const imageUrl = userProfile?.profileImage || userProfile?.profilePicture;
  const showProfileImage = !imageError && imageUrl && isValidUrl(imageUrl);

  const searchParams = new URLSearchParams(location.search);
  const settingsTab = searchParams.get("tab");
  const isSettingsSubPage = location.pathname.startsWith("/settings") && Boolean(settingsTab);
  const isProfilePage = location.pathname === '/profile' || location.pathname === '/view-profile';
  const showSubPageBackHeader = isProfilePage || isSettingsSubPage;

  const normalizedPath = location.pathname.toLowerCase();

  const pageTitle =
    PAGE_TITLES[location.pathname] ||
    Object.entries(PAGE_TITLES).find(([path]) =>
      normalizedPath.startsWith(path.toLowerCase())
    )?.[1] ||
    "Hoowlr";

  const currentSettingsTitle = settingsTab ? (SETTINGS_TAB_TITLES[settingsTab] || "Settings") : "Account settings";
  const resolvedPageTitle = location.pathname.startsWith("/settings")
    ? currentSettingsTitle
    : pageTitle;

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 relative z-[9999] lg:hidden">
      {/* Mobile header */}
      <div className="flex lg:hidden items-center justify-between px-4 h-20">
        {showSubPageBackHeader ? (
          <>
            <button
              type="button"
              onClick={() => {
                if (isSettingsSubPage) {
                  navigate("/settings");
                } else {
                  navigate(-1);
                }
              }}
              className="size-9 min-w-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-colors"
              aria-label="Back"
            >
              <ArrowLeftIcon className="w-4 h-4" />
            </button>

            <span className="text-xl font-bold text-gray-900 font-['Plus_Jakarta_Sans'] line-clamp-1 text-center">
              {resolvedPageTitle}
            </span>

            <div className="size-9" />
          </>
        ) : (
          <>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#EBEBEC]">
              {showProfileImage ? (
                <img
                  src={imageUrl}
                  alt="Profile"
                  className="h-8 w-8 rounded-full object-cover"
                  onError={() => setImageError(true)}
                  referrerPolicy="no-referrer"
                />
              ) : (
                <img src={personIcon} alt="Profile" className="h-4 w-4" />
              )}
            </div>

            <span className="text-lg sm:text-xl font-bold text-gray-900 font-['Plus_Jakarta_Sans'] line-clamp-1 text-center mx-2 flex-1">
              {resolvedPageTitle}
            </span>

            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
              <button
                type="button"
                onClick={onOpenMobileMenu}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                aria-label="Open menu"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
            </div>
          </>
        )}
      </div>

    </header>
  );
};

export default Header;